import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Bank2, CreditCard2Front, ExclamationTriangleFill, InfoCircleFill } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { PaymentMethodResp, SubListEntry } from "./Data/ApiTypes";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { countSubscriptionsBlockingPaymentMethodRemoval, countPaymentMethodSubscriptions, filterPaymentMethodSubscriptions, showSubscriptionStatus } from "./Data/Subscription";
import { cents, showMoney } from "./Data/Money";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";

interface CardDetailsModalProps {
    pm:       PaymentMethodResp | null;
    onHide:   () => void;
    onDelete: () => void;
    onMove?:  () => void;
}

function getCardLogo( brand: string ) {
    switch( brand ) {
        case "visa":       return "/img/card-logo-visa.svg";
        case "mastercard": return "/img/card-logo-mastercard.svg";
        case "amex":       return "/img/card-logo-american-express.svg";
        case "discover":   return "/img/card-logo-discover.png";
        default:           return null;
    }
}

function CompactPmRow( { pm }: { pm: PaymentMethodResp } ) {
    if( pm.card ) {
        const logo = getCardLogo( pm.card.brand );
        const expMonth = pm.card.expirationMonth;
        const expYear  = pm.card.expirationYear.toString().substring( 2 );
        return (
            <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center flex-shrink-0"
                     style={{ width: 48, height: 32 }}>
                    {logo
                        ? <img src={logo} style={{ maxWidth: 48, maxHeight: 32, objectFit: "contain" }} />
                        : <CreditCard2Front size={24} className="text-secondary" />}
                </div>
                <div>
                    <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                        •••• {pm.card.last4Digits}
                    </div>
                    <div className="text-secondary" style={{ fontSize: "0.8rem" }}>
                        Expires {expMonth}/{expYear}
                    </div>
                </div>
            </div>
        );
    }
    if( pm.usBankAccount ) {
        return (
            <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center flex-shrink-0"
                     style={{ width: 48, height: 32 }}>
                    <Bank2 size={24} className="text-secondary" />
                </div>
                <div>
                    <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                        •••• {pm.usBankAccount.last4Digits}
                    </div>
                    <div className="text-secondary" style={{ fontSize: "0.8rem" }}>
                        {pm.usBankAccount.bank}
                    </div>
                </div>
            </div>
        );
    }
    return null;
}

function SubRow( { sub }: { sub: SubListEntry } ) {
    const price    = sub.cachedMrr > 0 ? sub.cachedMrr : sub.price;
    const isActive = sub.status === SubscriptionStatus.Active;
    return (
        <div className="d-flex align-items-center gap-3 py-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
            <div className="flex-grow-1 min-w-0">
                <div className="fw-semibold text-truncate" style={{ fontSize: "0.9rem" }}>
                    {sub.plan.name}
                </div>
                <div className="text-secondary text-truncate" style={{ fontSize: "0.8rem" }}>
                    {sub.facility.name}
                </div>
            </div>
            <span className="flex-shrink-0 fw-semibold text-uppercase"
                  style={{ fontSize: "0.7rem", letterSpacing: "0.05em", color: isActive ? "#198754" : "#6c757d" }}>
                {showSubscriptionStatus( sub.status, sub.start )}
            </span>
            {price != null && (
                <div className="flex-shrink-0 text-end" style={{ minWidth: 72 }}>
                    <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>{showMoney( cents( price ) )}</div>
                    <div className="text-secondary" style={{ fontSize: "0.75rem" }}>monthly</div>
                </div>
            )}
        </div>
    );
}

export function UserBillingCardDetailsModal( { pm, onHide, onDelete, onMove }: CardDetailsModalProps ) {
    const [deleting, setDeleting] = useState( false );

    useEffect( () => {
        setDeleting( false );
    }, [pm?.paymentMethodId] );

    const visibleSubs    = pm ? filterPaymentMethodSubscriptions( pm.subscriptions ) : [];
    const pendingTransitionSubs = pm ? pm.subscriptions.filter( s => s.isPendingPaymentMethodVerification ) : [];
    const connectedCount = visibleSubs.length;
    const blockingCount  = pm ? countSubscriptionsBlockingPaymentMethodRemoval( pm.subscriptions ) : 0;
    const hasBlockingSubs = blockingCount > 0;

    const handleDelete = () => {
        if( !pm ) return;
        setDeleting( true );
        Api.stripePaymentMethodDelete( pm.paymentMethodId )
            .then( r => processResult( r,
                () => { onDelete(); onHide(); },
                err  => { alert( err ); setDeleting( false ); }
            ) );
    };

    if( !pm ) return null;

    const pmTypeLabel = pm.card ? "Card" : pm.usBankAccount ? "Bank account" : "Payment method";

    return (
        <Modal show={!!pm} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0 pb-1">
                <Modal.Title className="fs-5 fw-semibold">{pmTypeLabel} details</Modal.Title>
            </Modal.Header>

            <Modal.Body className="pt-0 pb-3 px-4">
                {/* Compact card row */}
                <div className="p-3 rounded-3 mb-4" style={{ border: "1px solid #e8e8e8", background: "#fafafa" }}>
                    <CompactPmRow pm={pm} />
                </div>

                {/* Subscriptions label */}
                <div className="text-uppercase fw-semibold mb-2"
                     style={{ fontSize: "0.72rem", letterSpacing: "0.08em", color: "#888" }}>
                    {connectedCount > 0 ? `Connected subscriptions (${connectedCount})` : "Subscriptions"}
                </div>

                {visibleSubs.length === 0 ? (
                    <div className="text-center text-secondary py-3 rounded-3"
                         style={{ fontSize: "0.88rem", background: "#f8f8f8" }}>
                        Not connected to any active subscriptions
                    </div>
                ) : (
                    <div>
                        {visibleSubs.map( sub => <SubRow key={sub.subscriptionId} sub={sub} /> )}
                    </div>
                )}

                {pendingTransitionSubs.length > 0 && (
                    <div className="d-flex align-items-start gap-2 mt-3 p-3 rounded-3"
                         style={{ background: "#fff8f0", border: "1px solid #ffd8a8", fontSize: "0.85rem", color: "#8a4b00" }}>
                        <InfoCircleFill className="flex-shrink-0 mt-1" style={{ color: "#fd7e14" }} />
                        <span>
                            {pendingTransitionSubs.length === 1
                                ? <>1 subscription is moving to a new bank account and is <strong>pending verification</strong>. It is no longer billed through this {pmTypeLabel.toLowerCase()}.</>
                                : <>{pendingTransitionSubs.length} subscriptions are moving to a new bank account and are <strong>pending verification</strong>. They are no longer billed through this {pmTypeLabel.toLowerCase()}.</>}
                        </span>
                    </div>
                )}

                {/* Warning */}
                {hasBlockingSubs && (
                    <div className="d-flex align-items-start gap-2 mt-3 p-3 rounded-3"
                         style={{ background: "#fff8e1", border: "1px solid #ffe082", fontSize: "0.85rem", color: "#5a4000" }}>
                        <ExclamationTriangleFill className="flex-shrink-0 mt-1" style={{ color: "#f59e0b" }} />
                        <span>
                            This {pmTypeLabel.toLowerCase()} is billing{" "}
                            <strong>{blockingCount} active {blockingCount === 1 ? "subscription" : "subscriptions"}</strong>.
                            Move {blockingCount === 1 ? "it" : "them"} to another payment method before removing this one.
                        </span>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0 px-4 pb-4 d-flex flex-column gap-2">
                {hasBlockingSubs && onMove && (
                    <Button className="w-100 btn-main" onClick={onMove}>
                        Move subscriptions
                    </Button>
                )}
                {hasBlockingSubs ? (
                    <div className="w-100 text-center py-2 rounded-4"
                         style={{
                             border: "2px solid #dee2e6",
                             color: "#adb5bd",
                             fontSize: "0.95rem",
                             fontWeight: 500,
                             cursor: "not-allowed",
                             userSelect: "none",
                         }}>
                        Remove {pmTypeLabel.toLowerCase()}
                    </div>
                ) : (
                    <Button
                        className="w-100 border-danger text-danger"
                        disabled={deleting}
                        loading={deleting}
                        loadingText="Removing…"
                        onClick={handleDelete}
                    >
                        Remove {pmTypeLabel.toLowerCase()}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
